<template>
  <div class='demo-app vx-card'>
    <div class='demo-app-main' id="legent-filter-card">

      <vx-card cardBorder noShadow class="mb-3 schedule-filters-card">
        <!-- Compact Top Controls: View Scope & Departments -->
        <div class="schedule-toolbar flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-wrap items-center gap-3">
            <!-- View Scope Toggle -->
            <div class="scope-segmented-control">
              <button
                type="button"
                v-for="s in scopes"
                :key="s.value"
                :class="['scope-segment-btn', { 'is-active': scope === s.value }]"
                @click="setScope(s.value)"
              >
                <feather-icon :icon="s.value === 'user' ? 'UserIcon' : 'GlobeIcon'" svgClasses="h-3.5 w-3.5 mr-1" />
                {{ s.label }}
              </button>
            </div>

            <!-- Departments Multi-Select -->
            <div class="department-filter-wrapper">
              <v-select
                multiple
                :closeOnSelect="false"
                placeholder="Filter by Department..."
                label="name"
                v-model="selectedDepartments"
                @input="onInputEventType($event)"
                :options="departments"
                :reduce="option => option.id"
                :dir="$vs.rtl ? 'rtl' : 'ltr'"
                class="schedule-dept-select"
              ></v-select>
            </div>

            <button
              v-if="selectedDepartments.length > 0"
              type="button"
              class="reset-dept-link"
              @click="clearDepartments"
              title="Clear department filter"
            >
              <feather-icon icon="XIcon" svgClasses="h-3 w-3 mr-0.5" />
              Reset Depts ({{ selectedDepartments.length }})
            </button>

            <!-- Presets Menu Dropdown -->
            <div class="schedule-preset-wrapper">
              <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
                <button
                  type="button"
                  class="preset-trigger-btn"
                  title="Saved View Presets"
                >
                  <feather-icon icon="BookmarkIcon" svgClasses="h-3.5 w-3.5 mr-1.5 text-primary" />
                  <span class="preset-name-label font-medium text-xs">{{ currentPresetLabel }}</span>
                  <feather-icon icon="ChevronDownIcon" svgClasses="h-3 w-3 ml-1.5 opacity-75" />
                </button>
                <vs-dropdown-menu class="schedule-preset-dropdown-menu">
                  <!-- Header: System Presets -->
                  <div class="preset-section-title">
                    <span>View Presets</span>
                  </div>

                  <!-- Built-in System Presets -->
                  <div class="preset-items-group">
                    <div
                      class="preset-item"
                      :class="{ 'is-active-preset': activePresetId === 'default_all' }"
                      @click="applyPreset({ id: 'default_all', name: 'All Tasks (Overview)', scope: 'all', selectedDepartments: [], currentTypes: Object.keys(eventTypes) })"
                    >
                      <div class="preset-item-content">
                        <feather-icon icon="GlobeIcon" class="preset-item-icon" />
                        <span class="preset-item-text">All Tasks (Overview)</span>
                      </div>
                      <feather-icon v-if="activePresetId === 'default_all'" icon="CheckIcon" class="preset-active-check" />
                    </div>

                    <div
                      class="preset-item"
                      :class="{ 'is-active-preset': activePresetId === 'default_user' }"
                      @click="applyPreset({ id: 'default_user', name: 'My Tasks Only', scope: 'user', selectedDepartments: [], currentTypes: Object.keys(eventTypes) })"
                    >
                      <div class="preset-item-content">
                        <feather-icon icon="UserIcon" class="preset-item-icon" />
                        <span class="preset-item-text">My Tasks Only</span>
                      </div>
                      <feather-icon v-if="activePresetId === 'default_user'" icon="CheckIcon" class="preset-active-check" />
                    </div>
                  </div>

                  <!-- Custom User Presets -->
                  <div v-if="customPresets && customPresets.length > 0" class="preset-custom-section">
                    <div class="preset-section-title">
                      <span>Custom Views</span>
                    </div>
                    <div class="preset-items-group">
                      <div
                        v-for="preset in customPresets"
                        :key="preset.id"
                        class="preset-item"
                        :class="{ 'is-active-preset': activePresetId === preset.id }"
                        @click="applyPreset(preset)"
                      >
                        <div class="preset-item-content">
                          <feather-icon icon="BookmarkIcon" class="preset-item-icon text-primary" />
                          <span class="preset-item-text" :title="preset.name">{{ preset.name }}</span>
                          <span v-if="preset.isDefault" class="preset-default-badge">Default</span>
                        </div>
                        <div class="preset-item-actions" @click.stop>
                          <feather-icon
                            v-if="activePresetId === preset.id"
                            icon="CheckIcon"
                            class="preset-active-check"
                          />
                          <button
                            type="button"
                            class="preset-action-icon-btn"
                            title="Delete preset"
                            @click.stop="deletePreset(preset.id)"
                          >
                            <feather-icon icon="Trash2Icon" class="preset-trash-icon" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Footer Actions -->
                  <div class="preset-dropdown-footer">
                    <button
                      type="button"
                      class="save-preset-btn"
                      @click="openSavePresetModal"
                    >
                      <feather-icon icon="PlusIcon" class="save-preset-icon" />
                      <span>Save Current View as Preset</span>
                    </button>
                  </div>
                </vs-dropdown-menu>
              </vs-dropdown>
            </div>
          </div>

          <!-- Quick Actions: Select/Clear All Types -->
          <div class="flex items-center gap-3 text-xs">
            <span class="text-muted font-medium">
              {{ currentTypes.length }}/{{ formatted_event_types.length }} visible
            </span>
            <button
              type="button"
              @click.stop="onClickSelectAll()"
              v-if="formatted_event_types.length !== currentTypes.length"
              class="legend-action-link text-primary font-medium"
            >
              Select All
            </button>
            <button
              type="button"
              @click.stop="onClickClearAll()"
              v-if="currentTypes.length > 0"
              class="legend-action-link text-danger font-medium"
            >
              Hide All
            </button>
          </div>
        </div>

        <!-- Minimalist Task Types Legend Filter Bar -->
        <div class="legend-bar mt-3 pt-2.5 border-t">
          <div class="legend-filters-wrapper">
            <div
              v-for="(event_type, index) in formatted_event_types"
              :key="index"
              class="legend-filter-pill"
              :class="{ 'is-active': isTypeSelected(event_type.type) }"
              @click="toggleTaskType(event_type.type)"
              role="checkbox"
              :aria-checked="isTypeSelected(event_type.type)"
              tabindex="0"
              @keydown.space.prevent="toggleTaskType(event_type.type)"
              @keydown.enter.prevent="toggleTaskType(event_type.type)"
              :title="isTypeSelected(event_type.type) ? 'Hide ' + event_type.type : 'Show ' + event_type.type"
            >
              <span
                class="legend-dot"
                :style="{
                  backgroundColor: isTypeSelected(event_type.type) ? event_type.color : 'transparent',
                  borderColor: event_type.color
                }"
              ></span>
              <span class="legend-text">{{ event_type.type }}</span>
            </div>
          </div>
        </div>
      </vx-card>

      <!-- <Legend :eventColors="eventTypes"></Legend> -->

      <FullCalendar ref="calendar" class='demo-app-calendar' :options='calendarOptions'>
        <template v-slot:eventContent='arg'>
          <span class="fc-event-group-badge" v-if="arg.event.extendedProps.isGroup">
            <span class="fc-event-count" :style="{ backgroundColor: eventColor(arg.event.extendedProps.type) }">
              {{ arg.event.extendedProps.count }}
            </span>
            <b class="fc-event-title-text" :style="{ color: eventColor(arg.event.extendedProps.type) }">
              {{ arg.event.title }}
            </b>
          </span>
          <span class="fc-event-single-badge" v-else :style="{ color: eventColor(arg.event.extendedProps.type) }">
            <b v-if="arg.timeText" class="fc-event-time-text">{{ arg.timeText }}</b>
            <span class="fc-event-title-text" :title="arg.event.title">{{ arg.event.title }}</span>
          </span>
        </template>
      </FullCalendar>
      <div id="custom-tooltip" class="custom-tooltip" style="display:none;"></div>
    </div>
    
    <!-- Grouped Tasks Details Modal -->
    <vs-popup
      class="grouped-schedule-events-popup"
      :title="selectedGroup ? `${selectedGroup.type} (${selectedGroup.items.length} Tasks) — ${selectedGroup.startDateFormatted}` : 'Grouped Tasks'"
      :active.sync="isGroupedTasksModalOpen"
      @close="isGroupedTasksModalOpen = false"
    >
      <div v-if="selectedGroup" class="grouped-tasks-container">
        <p class="text-sm opacity-75 mb-3">
          Select any task to open details:
        </p>
        <div class="grouped-task-list">
          <div
            v-for="(item, index) in selectedGroup.items"
            :key="item.id || index"
            class="grouped-task-card flex items-center justify-between p-3 mb-2 rounded border cursor-pointer hover:shadow-md transition-all"
            :style="{ borderLeft: `5px solid ${eventColor(item.type)}` }"
            @click="onClickGroupedItem(item)"
          >
            <div class="flex flex-col pr-2">
              <span class="font-medium text-dark">{{ item.title }}</span>
              <div class="flex items-center gap-2 mt-0.5" v-if="item.timeFormatted">
                <span class="text-xs font-semibold text-primary">⏰ {{ item.timeFormatted }}</span>
              </div>
            </div>
            <vs-button
              size="small"
              type="flat"
              icon-pack="feather"
              icon="icon-external-link"
              :color="eventColor(item.type)"
              @click.stop="onClickGroupedItem(item)"
            >
              Open
            </vs-button>
          </div>
        </div>
      </div>
    </vs-popup>

    <!-- Save Preset Modal -->
    <vs-popup
      class="save-schedule-preset-popup"
      title="Save View as Preset"
      :active.sync="isSavePresetModalOpen"
      @close="closeSavePresetModal"
    >
      <div class="preset-modal-body p-2">
        <p class="text-xs text-muted mb-3">
          Save your current active filters (Scope, Department filters, and Task types) as a named preset so you can switch to it with 1 click.
        </p>

        <div class="mb-4">
          <label class="text-xs font-semibold text-dark block mb-1">Preset Name *</label>
          <vs-input
            v-model="newPresetName"
            placeholder="e.g. Production Focus, Warehouse Tasks, HR Leave View"
            class="w-full"
            @keyup.enter="saveNewPreset"
          />
        </div>

        <!-- Preview of Current Filters -->
        <div class="current-filters-preview p-3 rounded bg-light border mb-4 text-xs">
          <div class="font-semibold text-dark mb-2">Filters included in this preset:</div>
          <div class="grid grid-cols-1 gap-1.5">
            <div class="flex items-center justify-between">
              <span class="text-muted">Scope:</span>
              <span class="font-medium text-dark">{{ scope === 'user' ? 'My Tasks' : 'All Tasks' }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-muted">Departments:</span>
              <span class="font-medium text-dark">
                {{ selectedDepartmentNames.length > 0 ? selectedDepartmentNames.join(', ') : 'All Departments (No filter)' }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-muted">Task Types:</span>
              <span class="font-medium text-dark">
                {{ currentTypes.length }}/{{ formatted_event_types.length }} types active
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center mb-4">
          <vs-checkbox v-model="newPresetIsDefault" class="text-xs">
            Set as default view when opening schedule
          </vs-checkbox>
        </div>

        <div class="flex justify-end gap-2 pt-2 border-t">
          <vs-button type="flat" color="secondary" size="small" @click="closeSavePresetModal">
            Cancel
          </vs-button>
          <vs-button
            color="primary"
            size="small"
            :disabled="!newPresetName || !newPresetName.trim()"
            @click="saveNewPreset"
          >
            Save Preset
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <ViewCompanyScheduleEventPopUp ref="viewCompanyScheduleEventPopUp" />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list';
import { createEventId } from './event-utils'
import vSelect from 'vue-select'

import { RepositoryFactory } from '@/repository/RepositoryFactory'
const CompanyScheduleRepository = RepositoryFactory.get('company_schedule');

import Legend from './Legand.vue'
import ViewCompanyScheduleEventPopUp from '@/components/popups/company-schedule/ViewCompanyScheduleEventPopUp.vue'

export default {

  components: {
    Legend,
    FullCalendar,
    'v-select': vSelect,
    ViewCompanyScheduleEventPopUp
  },

  data() {
    return {
      eventTypes: {
        'Raw Material Order': '#132440',
        'Scheduled Task': '#628141',
        'Other Order': '#F25912',
        'Sales Order': '#5C3E94',
        'Work Order': '#A27B5C',
        'Staff Task' : '#255F38',
        'Staff Leave': '#66005A',
        'Staff Birthday' : '#A2678A',
        'Closed Holidays': '#A64942'
      },
      isGroupedTasksModalOpen: false,
      selectedGroup: null,
      tooltipShowTimeout: null,
      tooltipHideTimeout: null,
      activeTooltipProps: null,
      calendarOptions: {
        height: 1000,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          listPlugin
        ],
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        },
        initialView: 'dayGridMonth',
        initialEvents: [], // alternatively, use the `events` setting to fetch from a feed
        editable: false,
        selectable: false,
        selectMirror: true,
        dayMaxEvents: 4, // limits visible events per day cell and adds a +more link
        moreLinkClick: 'popover',
        weekends: true,
        allDaySlot: true,
        nowIndicator: true,
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        },
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents,
        datesSet: this.handleDatesSet,
        eventDidMount: (info) => {
          const color = this.eventColor(info.event.groupId)

          info.el.style.borderColor = color;
          info.el.style.backgroundColor = color + '28';
          info.el.style.color = color;

          // Only apply for list views (like listWeek, listMonth, etc.)
          if (info.view.type.includes('list')) {
            const groupName = info.event.groupId; // e.g. "Product Order"
            // Sanitize for use in a class name: replace spaces/special chars
            const groupKey = groupName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');

            const prev = info.el.previousElementSibling;

            const needHeader =
              !prev || !prev.classList.contains(`group-${groupKey}`);

            if (needHeader) {
              const header = document.createElement('tr');
              header.classList.add('fc-list-group-header', `group-${groupKey}`);
              header.innerHTML = `
                <td colspan="3" style="
                  background:#f5f7fa;
                  font-weight:bold;
                  color:#333;
                  padding:6px 10px;
                  border-top:2px solid #d8dde6;
                  text-transform:uppercase;
                  font-size:13px;
                ">
                  ${groupName}
                </td>
              `;
              info.el.parentNode.insertBefore(header, info.el);
            }

            info.el.classList.add(`group-${groupKey}`);
          }

          // hide "All-day" in list views only
          if (info.view.type.includes('list') && info.event.allDay) {
            const timeEl = info.el.querySelector('.fc-list-event-time');
            if (timeEl && timeEl.innerText === 'all-day') {
              timeEl.style.display = 'none';
            }
          }

          // Tooltip on hover with hover-intent protection
          const props = info.event.extendedProps
          if (props.tooltip_label) {
            info.el.addEventListener('mouseenter', () => {
              this.onEventMouseEnter(info.el, props)
            })

            info.el.addEventListener('mouseleave', () => {
              this.onEventMouseLeave()
            })
          }
        }
      },
      currentEvents: [],
      currentStart: '',
      currentEnd: '',
      loadedEvents: [],
      currentTypes: [],
      isCompanySchedule: true, 
      scopes : [
      { label: 'My Tasks', value: 'user' },
      { label: 'All Tasks', value: 'all' }
      ],
      scope:'user',
      selectedDepartments: [],
      customPresets: [],
      activePresetId: null,
      isSavePresetModalOpen: false,
      newPresetName: '',
      newPresetIsDefault: false
    }
  },
  created() {
    this.loadSavedPresets()
    this.loadSavedPreferences()
  },
  async mounted() {
    const tooltip = document.getElementById('custom-tooltip')
    if (tooltip) {
      tooltip.addEventListener('mouseenter', () => {
        this.cancelShowTooltip()
        this.cancelHideTooltip()
      })
      tooltip.addEventListener('mouseleave', () => {
        this.scheduleHideTooltip()
      })
      tooltip.addEventListener('click', (e) => {
        const taskItem = e.target.closest('.tooltip-task-item')
        const moreNote = e.target.closest('.tooltip-more-note')
        const header = e.target.closest('.tooltip-header')

        if (taskItem && this.activeTooltipProps) {
          const idx = taskItem.getAttribute('data-index')
          if (idx !== null && this.activeTooltipProps.items && this.activeTooltipProps.items[idx]) {
            const item = this.activeTooltipProps.items[idx]
            this.hideTooltip()
            this.onClickGroupedItem(item)
            return
          }
        }

        if ((moreNote || header) && this.activeTooltipProps && this.activeTooltipProps.isGroup) {
          this.selectedGroup = {
            type: this.activeTooltipProps.type,
            startDateFormatted: this.activeTooltipProps.startDateFormatted,
            items: this.activeTooltipProps.items
          }
          this.isGroupedTasksModalOpen = true
          this.hideTooltip()
          return
        }

        if (this.activeTooltipProps && !this.activeTooltipProps.isGroup) {
          this.$refs.viewCompanyScheduleEventPopUp.openPopup({
            ...this.activeTooltipProps,
            backgroundColor: this.eventColor(this.activeTooltipProps.type)
          })
          this.hideTooltip()
        }
      })
    }
  },
  beforeDestroy() {
    this.cancelShowTooltip()
    this.cancelHideTooltip()
  },
  methods: {
    onEventMouseEnter(el, props) {
      // If a tooltip is already visible, delay switching (220ms) so transit across other events doesn't hijack hover
      const delay = this.activeTooltipProps ? 220 : 60
      this.cancelShowTooltip()
      this.cancelHideTooltip()

      this.tooltipShowTimeout = setTimeout(() => {
        this.showTooltip(el, props)
      }, delay)
    },
    onEventMouseLeave() {
      this.cancelShowTooltip()
      this.scheduleHideTooltip()
    },
    cancelShowTooltip() {
      if (this.tooltipShowTimeout) {
        clearTimeout(this.tooltipShowTimeout)
        this.tooltipShowTimeout = null
      }
    },
    showTooltip(targetEl, props) {
      this.cancelShowTooltip()
      this.cancelHideTooltip()
      this.activeTooltipProps = props
      const tooltip = document.getElementById('custom-tooltip')
      if (!tooltip || !props.tooltip_label) return

      tooltip.innerHTML = props.tooltip_label
      tooltip.style.display = 'block'
      this.$nextTick(() => {
        this.positionTooltip(targetEl)
      })
    },
    positionTooltip(targetEl) {
      const tooltip = document.getElementById('custom-tooltip')
      if (!tooltip || !targetEl) return

      const rect = targetEl.getBoundingClientRect()
      const tooltipWidth = tooltip.offsetWidth || 340
      const tooltipHeight = tooltip.offsetHeight || 280
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight

      const spaceRight = windowWidth - rect.right - 16
      const spaceLeft = rect.left - 16

      let top, left

      // Prefer side positioning to avoid crossing badges stacked vertically
      if (spaceRight >= tooltipWidth) {
        left = rect.right + 6
        top = rect.top - 6
      } else if (spaceLeft >= tooltipWidth) {
        left = rect.left - tooltipWidth - 6
        top = rect.top - 6
      } else {
        // Fallback to below
        left = rect.left
        top = rect.bottom + 4
      }

      // Keep within horizontal window bounds
      if (left + tooltipWidth > windowWidth - 12) {
        left = windowWidth - tooltipWidth - 12
      }
      if (left < 12) {
        left = 12
      }

      // Keep within vertical window bounds
      if (top + tooltipHeight > windowHeight - 12) {
        top = windowHeight - tooltipHeight - 12
      }
      if (top < 12) {
        top = 12
      }

      tooltip.style.top = `${top}px`
      tooltip.style.left = `${left}px`
    },
    scheduleHideTooltip() {
      this.cancelHideTooltip()
      this.tooltipHideTimeout = setTimeout(() => {
        this.hideTooltip()
      }, 300)
    },
    cancelHideTooltip() {
      if (this.tooltipHideTimeout) {
        clearTimeout(this.tooltipHideTimeout)
        this.tooltipHideTimeout = null
      }
    },
    hideTooltip() {
      this.cancelShowTooltip()
      this.cancelHideTooltip()
      const tooltip = document.getElementById('custom-tooltip')
      if (tooltip) {
        tooltip.style.display = 'none'
      }
      this.activeTooltipProps = null
    },
    async fetchScheduleEvents() {
      this.hideTooltip()
      await this.$vs.loading({
        container: '#legent-filter-card',
        scale: 0.45
      })
      try {
        const departmentIds = this.selectedDepartments && this.selectedDepartments.length > 0
          ? this.selectedDepartments.join(',')
          : ''
        const { data } = (await CompanyScheduleRepository.getListOfCompanySchedule(
          this.currentStart,
          this.currentEnd,
          this.currentTypes.length > 0 ? this.currentTypes.toString() : '',
          this.scope ? this.scope : undefined,
          departmentIds
        )).data

        this.loadedEvents = this.formatLoadedEvents(data)

        const calendarApi = this.$refs.calendar.getApi()
        calendarApi.removeAllEvents()
        calendarApi.addEventSource(this.loadedEvents)
      } catch (error) {
        console.error('Error fetching schedule events', error)
      } finally {
        this.$vs.loading.close('#legent-filter-card > .con-vs-loading')
      }
    },
    normalizeTime(timeStr) {
      if (!timeStr) return null
      const trimmed = String(timeStr).trim()
      if (trimmed === '' || trimmed === '00:00:00' || trimmed === '00:00' || trimmed === '0:00') return null
      const parts = trimmed.split(':')
      if (parts.length < 2) return null
      const hh = parts[0].padStart(2, '0')
      const mm = (parts[1] || '00').padStart(2, '0')
      const ss = (parts[2] || '00').padStart(2, '0')
      return `${hh}:${mm}:${ss}`
    },
    formatTimeDisplay(startTime, endTime) {
      const start = this.normalizeTime(startTime)
      const end = this.normalizeTime(endTime)
      if (start && end) {
        return `${start.slice(0, 5)} - ${end.slice(0, 5)}`
      } else if (end) {
        return `Due ${end.slice(0, 5)}`
      } else if (start) {
        return `At ${start.slice(0, 5)}`
      }
      return ''
    },
    formatLoadedEvents(events) {
      const validEvents = events.filter(e => !!e.start_date)

      // Only 'Staff Task' and 'Work Order' are eligible to have start/end times; all other types are all-day
      const timedEventTypes = ['Staff Task', 'Work Order']

      // Group events by (start_date + end_date + time + type)
      const groups = {}

      validEvents.forEach(e => {
        const [day, month, year] = e.start_date.split('/')
        const startISODate = `${year}-${month}-${day}`

        let endISODate = startISODate
        if (e.end_date) {
          const [d, m, y] = e.end_date.split('/')
          endISODate = `${y}-${m}-${d}`
        }

        const isTimeEligible = timedEventTypes.includes(e.type)
        const startTime = isTimeEligible ? this.normalizeTime(e.start_time) : null
        const endTime = isTimeEligible ? this.normalizeTime(e.end_time) : null
        const isTimed = !!(startTime || endTime)
        const timeFormatted = isTimeEligible ? this.formatTimeDisplay(e.start_time, e.end_time) : ''

        let startISO = startISODate
        let endISO = endISODate

        if (isTimed) {
          if (startTime && endTime) {
            startISO = `${startISODate}T${startTime}`
            endISO = `${endISODate}T${endTime}`
          } else if (endTime) {
            // Task has a due/end time (and start_time is 00:00:00 or empty):
            // Place event at the due time in the hourly grid
            startISO = `${endISODate}T${endTime}`
            endISO = undefined
          } else if (startTime) {
            startISO = `${startISODate}T${startTime}`
            endISO = undefined
          }
        }

        const titleStr = e.title || 'N/A'
        const key = `${startISO}_${endISO || ''}_${e.type}_${isTimed ? 'timed' : 'allday'}`

        if (!groups[key]) {
          groups[key] = {
            startISO,
            endISO,
            isTimed,
            timeFormatted,
            type: e.type,
            startDateFormatted: e.start_date,
            endDateFormatted: e.end_date,
            items: []
          }
        }

        groups[key].items.push({
          id: e.id,
          title: titleStr,
          type: e.type,
          data: e.data,
          startDateFormatted: e.start_date,
          endDateFormatted: e.end_date,
          start_time: isTimeEligible ? e.start_time : null,
          end_time: isTimeEligible ? e.end_time : null,
          timeFormatted
        })
      })

      // Map grouped items into FullCalendar event objects
      return Object.values(groups).map(group => {
        const isMultiple = group.items.length > 1
        const displayTitle = isMultiple
          ? `${group.type} (${group.items.length})`
          : group.items[0].title

        const dateSubDisplay = group.timeFormatted
          ? `${group.startDateFormatted} • ${group.timeFormatted}`
          : group.startDateFormatted

        // Rich tooltip preview
        let tooltip_label = null
        if (isMultiple) {
          const itemsList = group.items.map((item, idx) => `
            <li class="tooltip-task-item" data-index="${idx}" title="Click to open task details">
              <span class="tooltip-task-num">${idx + 1}.</span>
              <span class="tooltip-task-text">${item.title}</span>
              ${item.timeFormatted ? `<span class="tooltip-task-time" style="font-size: 11px; opacity: 0.8; margin-left: 4px;">(${item.timeFormatted})</span>` : ''}
            </li>
          `).join('')

          tooltip_label = `
            <div class="tooltip-container">
              <div class="tooltip-header" style="border-left: 4px solid ${this.eventColor(group.type)}; padding-left: 6px; cursor: pointer;" title="Click to view all tasks">
                <strong>${group.type}</strong> (${group.items.length} tasks)
              </div>
              <div class="tooltip-date-sub">${dateSubDisplay}</div>
              <ul class="tooltip-list">
                ${itemsList}
              </ul>
              <div class="tooltip-more-note" title="Click to view all in modal">
                <span>View all ${group.items.length} tasks in modal</span>
                <span style="font-size: 11px;">➔</span>
              </div>
            </div>
          `
        } else {
          const single = group.items[0]
          const singleDateSubDisplay = single.timeFormatted
            ? `${group.startDateFormatted} • ${single.timeFormatted}`
            : group.startDateFormatted

          if (single.type === 'Staff Leave' && single.data && single.data.staff && single.data.staff.length > 0) {
            const staffList = single.data.staff.map(staff => `
              <li class="tooltip-task-item">
                <span class="tooltip-task-text">${staff.name}</span>
              </li>
            `).join('')

            tooltip_label = `
              <div class="tooltip-container">
                <div class="tooltip-header" style="border-left: 4px solid ${this.eventColor(single.type)}; padding-left: 6px;">
                  <strong>Staff on Leave</strong>
                </div>
                <div class="tooltip-date-sub">${singleDateSubDisplay}</div>
                <ul class="tooltip-list">
                  ${staffList}
                </ul>
              </div>
            `
          } else {
            tooltip_label = `
              <div class="tooltip-container">
                <div class="tooltip-header" style="border-left: 4px solid ${this.eventColor(single.type)}; padding-left: 6px; cursor: pointer;">
                  <strong>${single.type}</strong>
                </div>
                <div class="tooltip-date-sub">${singleDateSubDisplay}</div>
                <div class="tooltip-item-title">${single.title}</div>
              </div>
            `
          }
        }

        return {
          id: createEventId(),
          title: displayTitle,
          start: group.startISO,
          end: group.endISO || undefined,
          allDay: !group.isTimed,
          groupId: group.type,
          extendedProps: {
            type: group.type,
            isGroup: isMultiple,
            isTimed: group.isTimed,
            timeFormatted: group.timeFormatted,
            count: group.items.length,
            items: group.items,
            id: isMultiple ? null : group.items[0].id,
            data: isMultiple ? null : group.items[0].data,
            title: displayTitle,
            tooltip_label,
            startDateFormatted: group.startDateFormatted,
            endDateFormatted: group.endDateFormatted,
            start_time: isMultiple ? null : group.items[0].start_time,
            end_time: isMultiple ? null : group.items[0].end_time
          }
        }
      })
    },
    async handleDatesSet(info) {
      this.hideTooltip()
      const formatDate = (date) => {
        const d = new Date(date)
        const day = String(d.getDate()).padStart(2, '0')
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const year = d.getFullYear()
        return `${day}/${month}/${year}`
      }
      const formattedStart = formatDate(info.start)
      const formattedEnd = formatDate(info.end)

      this.currentStart = formattedStart
      this.currentEnd = formattedEnd

      await this.fetchScheduleEvents()
    },
    handleWeekendsToggle() {
      this.calendarOptions.weekends = !this.calendarOptions.weekends
    },

    handleDateSelect(selectInfo) {
      let title = prompt('Please enter a new title for your event')
      let calendarApi = selectInfo.view.calendar

      calendarApi.unselect()

      if (title) {
        calendarApi.addEvent({
          id: createEventId(),
          title,
          start: selectInfo.startStr,
          end: selectInfo.endStr,
          allDay: selectInfo.allDay
        })
      }
    },

    handleEventClick(clickInfo) {
      this.hideTooltip()
      const props = clickInfo.event.extendedProps
      if (props.isGroup && props.items && props.items.length > 1) {
        this.selectedGroup = {
          type: props.type,
          startDateFormatted: props.startDateFormatted,
          items: props.items
        }
        this.isGroupedTasksModalOpen = true
      } else {
        this.$refs.viewCompanyScheduleEventPopUp.openPopup({
          ...props,
          backgroundColor: this.eventColor(props.type),
        })
      }
    },

    onClickGroupedItem(item) {
      this.$refs.viewCompanyScheduleEventPopUp.openPopup({
        ...item,
        type: item.type,
        id: item.id,
        title: item.title,
        data: item.data,
        backgroundColor: this.eventColor(item.type)
      })
    },

    handleEvents(events) {
      this.currentEvents = events
    },
    async onInputEventType(key) {
      await this.fetchScheduleEvents()
    },
    lightenColor(hex, percent = 30) {
      const num = parseInt(hex.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const G = (num >> 8 & 0x00FF) + amt;
      const B = (num & 0x0000FF) + amt;
      return `#${(
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      ).toString(16).slice(1)}`;
    },
    eventColor(groupId) {
      return this.eventTypes[groupId] || '#419197';
    },
    isTypeSelected(type) {
      return this.currentTypes.includes(type)
    },
    async toggleTaskType(type) {
      const index = this.currentTypes.indexOf(type)
      if (index > -1) {
        this.currentTypes.splice(index, 1)
      } else {
        this.currentTypes.push(type)
      }
      await this.onInputEventType()
    },
    getChipStyle(eventType) {
      const isSelected = this.isTypeSelected(eventType.type)
      if (isSelected) {
        return {
          borderColor: eventType.color,
          backgroundColor: `${eventType.color}20`,
          boxShadow: `0 2px 8px ${eventType.color}35`
        }
      }
      return {}
    },
    getCheckboxStyle(eventType) {
      const isSelected = this.isTypeSelected(eventType.type)
      if (isSelected) {
        return {
          backgroundColor: eventType.color,
          borderColor: eventType.color,
          color: '#ffffff'
        }
      }
      return {}
    },
    async setScope(val) {
      if (this.scope !== val) {
        this.scope = val
        await this.fetchScheduleEvents()
      }
    },
    async clearDepartments() {
      this.selectedDepartments = []
      await this.fetchScheduleEvents()
    },
    async onClickSelectAll() {
      this.currentTypes = Object.keys(this.eventTypes)
      this.scope = this.scope || 'user'
      await this.fetchScheduleEvents()
    },
    async onClickClearAll() {
      this.currentTypes = []
      await this.fetchScheduleEvents()
    },
    getStorageKey() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const appUser = (this.$store && this.$store.state && this.$store.state.AppActiveUser) || null
      const userId = userInfo.id || userInfo.uid || (appUser && appUser.id) || 'default'
      return `nimble_my_schedule_pref_${userId}`
    },
    loadSavedPreferences() {
      try {
        const raw = localStorage.getItem(this.getStorageKey())
        if (!raw) {
          // If a custom default preset exists, apply it on initial load
          const defaultPreset = this.customPresets && this.customPresets.find(p => p.isDefault)
          if (defaultPreset) {
            this.scope = defaultPreset.scope || 'user'
            this.selectedDepartments = Array.isArray(defaultPreset.selectedDepartments) ? [...defaultPreset.selectedDepartments] : []
            if (Array.isArray(defaultPreset.currentTypes)) {
              const validTypes = Object.keys(this.eventTypes)
              this.currentTypes = defaultPreset.currentTypes.filter(t => validTypes.includes(t))
            } else {
              this.currentTypes = Object.keys(this.eventTypes)
            }
            this.activePresetId = defaultPreset.id
            return
          }

          // Otherwise default to standard initial view
          this.currentTypes = Object.keys(this.eventTypes)
          this.activePresetId = this.scope === 'all' ? 'default_all' : 'default_user'
          return
        }
        const saved = JSON.parse(raw)

        // 1. Scope ('user' | 'all')
        if (saved.scope && ['user', 'all'].includes(saved.scope)) {
          this.scope = saved.scope
        }

        // 2. Selected Departments (array of IDs)
        if (Array.isArray(saved.selectedDepartments)) {
          this.selectedDepartments = saved.selectedDepartments
        }

        // 3. Current Types (validate against known eventTypes)
        if (Array.isArray(saved.currentTypes)) {
          const validTypes = Object.keys(this.eventTypes)
          this.currentTypes = saved.currentTypes.filter(t => validTypes.includes(t))
        } else {
          this.currentTypes = Object.keys(this.eventTypes)
        }

        // 4. Set active preset ID or detect match
        if (saved.activePresetId) {
          this.activePresetId = saved.activePresetId
        } else {
          this.detectMatchingPreset()
        }
      } catch (e) {
        console.warn('Failed to load schedule preferences:', e)
        this.currentTypes = Object.keys(this.eventTypes)
        this.activePresetId = this.scope === 'all' ? 'default_all' : 'default_user'
      }
    },
    savePreferences() {
      try {
        const payload = {
          scope: this.scope,
          selectedDepartments: this.selectedDepartments,
          currentTypes: this.currentTypes,
          activePresetId: this.activePresetId
        }
        localStorage.setItem(this.getStorageKey(), JSON.stringify(payload))
      } catch (e) {
        console.warn('Failed to save schedule preferences:', e)
      }
    },
    detectMatchingPreset() {
      // 1. Check custom presets for an exact match
      if (this.customPresets && this.customPresets.length > 0) {
        const match = this.customPresets.find(p => {
          if (p.scope !== this.scope) return false
          const deptMatch = JSON.stringify(p.selectedDepartments || []) === JSON.stringify(this.selectedDepartments || [])
          if (!deptMatch) return false
          const pTypes = (p.currentTypes || []).slice().sort()
          const cTypes = (this.currentTypes || []).slice().sort()
          return JSON.stringify(pTypes) === JSON.stringify(cTypes)
        })
        if (match) {
          this.activePresetId = match.id
          return
        }
      }

      // 2. Check built-in presets
      const allTypes = Object.keys(this.eventTypes).slice().sort()
      const currentSortedTypes = (this.currentTypes || []).slice().sort()
      const isAllTypes = JSON.stringify(allTypes) === JSON.stringify(currentSortedTypes)
      const noDepts = !this.selectedDepartments || this.selectedDepartments.length === 0

      if (noDepts && isAllTypes) {
        this.activePresetId = this.scope === 'all' ? 'default_all' : 'default_user'
        return
      }

      this.activePresetId = null
    },
    getPresetsStorageKey() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const appUser = (this.$store && this.$store.state && this.$store.state.AppActiveUser) || null
      const userId = userInfo.id || userInfo.uid || (appUser && appUser.id) || 'default'
      return `nimble_my_schedule_custom_presets_${userId}`
    },
    loadSavedPresets() {
      try {
        const raw = localStorage.getItem(this.getPresetsStorageKey())
        if (raw) {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            this.customPresets = parsed
          }
        }
      } catch (e) {
        console.warn('Failed to load schedule custom presets:', e)
      }
    },
    savePresetsToStorage() {
      try {
        localStorage.setItem(this.getPresetsStorageKey(), JSON.stringify(this.customPresets))
      } catch (e) {
        console.warn('Failed to save schedule custom presets:', e)
      }
    },
    async applyPreset(preset) {
      if (!preset) return
      this.activePresetId = preset.id
      this.scope = preset.scope || 'user'
      this.selectedDepartments = Array.isArray(preset.selectedDepartments) ? [...preset.selectedDepartments] : []

      if (Array.isArray(preset.currentTypes)) {
        const validTypes = Object.keys(this.eventTypes)
        this.currentTypes = preset.currentTypes.filter(t => validTypes.includes(t))
      } else {
        this.currentTypes = Object.keys(this.eventTypes)
      }

      this.savePreferences()
      await this.fetchScheduleEvents()
    },
    openSavePresetModal() {
      this.newPresetName = ''
      this.newPresetIsDefault = false
      this.isSavePresetModalOpen = true
    },
    closeSavePresetModal() {
      this.isSavePresetModalOpen = false
      this.newPresetName = ''
      this.newPresetIsDefault = false
    },
    saveNewPreset() {
      if (!this.newPresetName || !this.newPresetName.trim()) {
        return
      }

      const presetId = `preset_${new Date().getTime()}`

      if (this.newPresetIsDefault) {
        this.customPresets.forEach(p => {
          p.isDefault = false
        })
      }

      const newPreset = {
        id: presetId,
        name: this.newPresetName.trim(),
        scope: this.scope,
        selectedDepartments: [...this.selectedDepartments],
        currentTypes: [...this.currentTypes],
        isDefault: !!this.newPresetIsDefault
      }

      this.customPresets.push(newPreset)
      this.activePresetId = presetId
      this.savePresetsToStorage()
      this.closeSavePresetModal()
    },
    deletePreset(presetId) {
      const idx = this.customPresets.findIndex(p => p.id === presetId)
      if (idx > -1) {
        this.customPresets.splice(idx, 1)
        if (this.activePresetId === presetId) {
          this.activePresetId = null
        }
        this.savePresetsToStorage()
      }
    }
  },
  watch: {
    scope() {
      this.detectMatchingPreset()
      this.savePreferences()
    },
    selectedDepartments: {
      handler() {
        this.detectMatchingPreset()
        this.savePreferences()
      },
      deep: true
    },
    currentTypes: {
      handler() {
        this.detectMatchingPreset()
        this.savePreferences()
      },
      deep: true
    }
  },
  computed: {
    departments() {
      return (this.$store && this.$store.getters && this.$store.getters.currentUser && this.$store.getters.currentUser.departments)
        ? this.$store.getters.currentUser.departments
        : []
    },
    currentPresetLabel() {
      if (this.activePresetId === 'default_all') return 'All Tasks (Overview)'
      if (this.activePresetId === 'default_user') return 'My Tasks Only'
      if (this.customPresets && this.customPresets.length > 0) {
        const found = this.customPresets.find(p => p.id === this.activePresetId)
        if (found) return found.name
      }
      return this.scope === 'all' ? 'All Tasks' : 'My Tasks'
    },
    selectedDepartmentNames() {
      if (!this.selectedDepartments || this.selectedDepartments.length === 0 || !this.departments) {
        return []
      }
      return this.selectedDepartments.map(id => {
        const d = this.departments.find(item => item.id === id)
        return d ? d.name : id
      })
    },
    formatted_event_types() {
      return Object.entries(this.eventTypes).map(([type, color]) => ({
        type,
        color
      }));
    }
  }
}
</script>

<style lang="scss" scoped>
h2 {
  margin: 0;
  font-size: 16px;
}

ul {
  margin: 0;
  padding: 0 0 0 1.5em;
}

li {
  margin: 1.5em 0;
  padding: 0;
}

b {
  margin-right: 3px;
}

.demo-app {
  display: flex;
  min-height: 100%;
  font-family: Arial, Helvetica Neue, Helvetica, sans-serif;
  font-size: 14px;
  height: 100%
}

.demo-app-sidebar {
  line-height: 1.5;
  background: #eaf9ff;
  border-right: 1px solid #d3e2e8;
}

.demo-app-sidebar-section {
  padding: 2em;
}

.demo-app-main {
  flex-grow: 1;
  padding: 2em;
}

.fc {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  max-width: none;
}

.fc .fc-button {
  background: #39b24b !important;
  border-color: #39b24b !important;
  color: #ffffff !important;
  font-weight: 500;
  text-transform: capitalize;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(57, 178, 75, 0.25) !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #2f993f !important;
    border-color: #2f993f !important;
    box-shadow: 0 4px 12px rgba(57, 178, 75, 0.35) !important;
  }

  &.fc-button-active {
    background: #278536 !important;
    border-color: #278536 !important;
  }

  &:disabled {
    opacity: 0.6 !important;
    box-shadow: none !important;
  }
}

.fc-list-group-header td {
  background: #f5f7fa !important;
  font-weight: 600;
  color: #333 !important;
  border-top: 2px solid #d8dde6 !important;
}

.fc-event-group-badge,
.fc-event-single-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  overflow: hidden;
}

.fc-event-count {
  color: #fff !important;
  font-size: 10.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  line-height: 1.2;
  flex-shrink: 0;
}

.fc-event-time-text {
  font-size: 11px;
  font-weight: 700;
  padding: 0 4px;
  background: rgba(0, 0, 0, 0.07);
  border-radius: 4px;
  line-height: 1.3;
  flex-shrink: 0;
  letter-spacing: 0.2px;
}

.fc-event-title-text {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.1px;
}
</style>

<style lang="scss">
.fc-list-day-cushion {
  margin-top: 40px !important;
  font-size: 15px !important;
}

.fc-list-event:hover td {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: inherit !important;
  backdrop-filter: brightness(1.2);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.fc-event, 
.fc-event .fc-event-title {
  white-space: normal;
  word-break: break-word;
  overflow: hidden;
}

.custom-tooltip {
  position: fixed;
  background: #ffffff;
  color: #333333;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.18);
  z-index: 99999;
  pointer-events: auto;
  width: 340px;
  max-width: 380px;
  max-height: 380px;
  white-space: normal;
  transition: opacity 0.15s ease;
  box-sizing: border-box;
}

/* Invisible bridge buffer to prevent hover drop when moving pointer */
.custom-tooltip::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  z-index: -1;
}

.tooltip-container {
  display: flex;
  flex-direction: column;
}

.tooltip-header {
  font-weight: 600;
  font-size: 13.5px;
  margin-bottom: 2px;
  color: #1e293b;
}

.tooltip-date-sub {
  font-size: 11.5px;
  color: #64748b;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #f1f5f9;
}

.tooltip-item-title {
  font-size: 12.5px;
  color: #334155;
  line-height: 1.4;
}

.tooltip-list {
  margin: 0 !important;
  padding: 0 !important;
  list-style: none;
  max-height: 220px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.tooltip-list::-webkit-scrollbar {
  width: 5px;
}
.tooltip-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}
.tooltip-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.tooltip-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.tooltip-task-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 2px 0 !important;
  padding: 4px 6px !important;
  border-radius: 4px;
  line-height: 1.35;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.tooltip-task-item:hover {
  background: #f1f5f9;
}

.tooltip-task-num {
  font-weight: 600;
  color: #94a3b8;
  min-width: 16px;
}

.tooltip-task-text {
  color: #334155;
  word-break: break-word;
}

.tooltip-more-note {
  margin-top: 8px;
  padding-top: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #3b82f6;
  border-top: 1px dashed #e2e8f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: color 0.15s ease;
}

.tooltip-more-note:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.fc-daygrid-event {
  border-radius: 6px !important;
  padding: 3px 6px !important;
  font-weight: 600;
  cursor: pointer;
  border-width: 1.5px !important;
  border-style: solid !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease !important;
}

.fc-daygrid-event:hover {
  transform: translateY(-1px);
  filter: brightness(1.12);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12) !important;
}

/* FullCalendar More Link styling */
.fc-daygrid-more-link {
  background: rgba(115, 103, 240, 0.12) !important;
  color: #7367f0 !important;
  font-weight: 600 !important;
  font-size: 11.5px !important;
  padding: 2px 8px !important;
  border-radius: 12px !important;
  margin-top: 2px !important;
  transition: all 0.2s ease;
  display: inline-block;
}

.fc-daygrid-more-link:hover {
  background: #7367f0 !important;
  color: #ffffff !important;
}

/* FullCalendar Popover Styling */
.fc-popover {
  border-radius: 8px !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid #e2e8f0 !important;
  overflow: hidden;
  z-index: 10000 !important;
}

.fc-popover-header {
  background: #f8fafc !important;
  padding: 8px 12px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.fc-popover-body {
  padding: 8px !important;
  max-height: 300px;
  overflow-y: auto;
}

/* Grouped Tasks Modal Styling */
.grouped-schedule-events-popup .vs-popup {
  min-width: 450px !important;
  max-width: 600px !important;
}

.grouped-task-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;
}

.grouped-task-card {
  background: #ffffff;
  border-color: #e2e8f0;
  transition: all 0.2s ease;
}

.grouped-task-card:hover {
  background: #f8fafc;
  transform: translateY(-1px);
}

/* Schedule Filters Card & Toolbar */
.schedule-filters-card {
  border-radius: 8px;
  border: 1px solid #dae1e7;

  .vx-card__body {
    padding: 0.9rem 1.25rem !important;
  }
}

.schedule-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Scope Segmented Control Capsule */
.scope-segmented-control {
  display: inline-flex;
  align-items: center;
  background-color: #f8f8f8;
  padding: 3px;
  border-radius: 8px;
  border: 1px solid #dae1e7;
  height: 36px;
}

.scope-segment-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: #626262;
  padding: 4px 12px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  white-space: nowrap;

  &:hover {
    color: #39b24b;
  }

  &.is-active {
    background-color: #ffffff;
    color: #39b24b;
    font-weight: 600;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
}

/* Department Select */
.department-filter-wrapper {
  min-width: 240px;
  max-width: 440px;
  flex: 1 1 auto;
}

.schedule-dept-select {
  .vs__dropdown-toggle {
    border-radius: 8px;
    border: 1px solid #dae1e7;
    min-height: 36px;
    height: auto;
    background-color: #ffffff;
    padding: 2px 4px;
    font-size: 12px;
  }

  .vs__selected-options {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 0;
    gap: 3px;
  }

  .vs__selected {
    background: rgba(57, 178, 75, 0.12);
    color: #39b24b;
    border: none;
    font-size: 11px;
    font-weight: 500;
    border-radius: 4px;
    padding: 2px 6px;
    margin: 2px 1px;
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
  }

  .vs__deselect {
    margin-left: 4px;
  }

  .vs__search {
    font-size: 12px;
    margin: 2px 0;
    padding: 0 4px;
    min-width: 80px;
  }

  .vs__actions {
    padding: 0 4px;
    align-self: center;
  }
}

.reset-dept-link {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  color: #ea5455;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: all 0.15s ease;
  white-space: nowrap;
  align-self: center;

  &:hover {
    text-decoration: underline;
  }
}

/* Preset Dropdown & Trigger */
.schedule-preset-wrapper {
  display: inline-flex;
  align-items: center;
}

.preset-trigger-btn {
  display: inline-flex;
  align-items: center;
  background-color: #f8fafc;
  color: #334155;
  border: 1px solid #dae1e7;
  border-radius: 8px;
  padding: 0 10px;
  height: 36px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;

  &:hover {
    border-color: #39b24b;
    background-color: #ffffff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }
}

.schedule-preset-dropdown-menu {
  min-width: 250px !important;
  max-width: 300px !important;
  padding: 8px 0 0 0 !important;
  border-radius: 10px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.14) !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff;
}

.preset-section-title {
  padding: 6px 14px 4px 14px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
}

.preset-custom-section {
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
  padding-top: 4px;
}

.preset-items-group {
  padding: 2px 6px;
}

.preset-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 2px;
  transition: all 0.15s ease;
  color: #334155;

  &:hover {
    background-color: #f1f5f9;
    color: #1e293b;
  }

  &.is-active-preset {
    background-color: rgba(57, 178, 75, 0.1);
    color: #278536;
    font-weight: 600;

    .preset-item-icon {
      color: #278536 !important;
    }
  }
}

.preset-item-content {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  flex: 1 1 auto;
}

.preset-item-icon {
  width: 15px !important;
  height: 15px !important;
  flex-shrink: 0;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 15px !important;
    height: 15px !important;
  }
}

.preset-item-text {
  font-size: 12.5px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preset-default-badge {
  font-size: 9.5px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 5px;
  border-radius: 4px;
  background-color: rgba(57, 178, 75, 0.15);
  color: #278536;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.preset-item-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 8px;
}

.preset-active-check {
  width: 14px !important;
  height: 14px !important;
  color: #278536;
  flex-shrink: 0;

  svg {
    width: 14px !important;
    height: 14px !important;
  }
}

.preset-action-icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(234, 84, 85, 0.12);
    color: #ea5455;
  }
}

.preset-trash-icon {
  width: 13px !important;
  height: 13px !important;

  svg {
    width: 13px !important;
    height: 13px !important;
  }
}

.preset-dropdown-footer {
  border-top: 1px solid #f1f5f9;
  padding: 8px;
  margin-top: 4px;
  background-color: #fafbfc;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.save-preset-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 600;
  background-color: #ffffff;
  color: #278536;
  border: 1px solid #39b24b;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    background-color: #39b24b;
    color: #ffffff;
    box-shadow: 0 2px 6px rgba(57, 178, 75, 0.25);
  }
}

.save-preset-icon {
  width: 13px !important;
  height: 13px !important;

  svg {
    width: 13px !important;
    height: 13px !important;
  }
}

.current-filters-preview {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.save-schedule-preset-popup .vs-popup {
  min-width: 420px !important;
  max-width: 500px !important;
}

.legend-action-link {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  transition: all 0.15s ease;
  font-size: 11.5px;

  &:hover {
    text-decoration: underline;
  }
}

/* Task Types Legend Filter Bar */
.legend-bar {
  border-top: 1px solid #edf2f7;
  width: 100%;
  clear: both;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
}

.legend-filters-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  align-items: center;
}

.legend-filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 14px;
  font-size: 11.5px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
  background: transparent;
  color: #5e5873;
  border: 1px solid transparent;

  &:hover {
    background: #f1f5f9;
    color: #2c2c2c;
  }

  &.is-active {
    font-weight: 600;
    color: #2c2c2c;
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.06);

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  &:not(.is-active) {
    color: #626262;
    opacity: 0.85;

    .legend-text {
      text-decoration: line-through;
    }

    &:hover {
      opacity: 1;
      color: #2c2c2c;
      background: #f1f5f9;
    }
  }
}

.legend-dot {
  width: 8.5px;
  height: 8.5px;
  border-radius: 50%;
  border: 2px solid;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.legend-text {
  white-space: nowrap;
  letter-spacing: 0.1px;
}

/* FullCalendar More Link styling */
.fc-daygrid-more-link {
  background: rgba(57, 178, 75, 0.12) !important;
  color: #39b24b !important;
  font-weight: 600 !important;
  font-size: 11.5px !important;
  padding: 2px 8px !important;
  border-radius: 12px !important;
  margin-top: 2px !important;
  transition: all 0.2s ease;
  display: inline-block;

  &:hover {
    background: #39b24b !important;
    color: #ffffff !important;
  }
}

/* Dark Theme Support */
.theme-dark {
  .schedule-filters-card {
    background-color: #10163a;
    border-color: #414561;
  }

  .legend-bar {
    border-top-color: #414561;
  }

  .scope-segmented-control {
    background-color: #171e49;
    border-color: #414561;
  }

  .scope-segment-btn {
    color: #c2c6dc;

    &:hover {
      color: #4ade80;
    }

    &.is-active {
      background-color: #262c49;
      color: #4ade80;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    }
  }

  .schedule-dept-select {
    .vs__dropdown-toggle {
      background-color: #171e49;
      border-color: #414561;
    }

    .vs__selected {
      background: rgba(57, 178, 75, 0.25);
      color: #86efac;
    }

    .vs__search,
    .vs__search::placeholder {
      color: #c2c6dc;
    }
  }

  .legend-filter-pill {
    color: #c2c6dc;

    &:hover {
      background: #171e49;
      color: #ebeefd;
    }

    &.is-active {
      color: #ebeefd;
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.08);

      &:hover {
        background: rgba(255, 255, 255, 0.09);
      }
    }

    &:not(.is-active) {
      color: #94a3b8;
      opacity: 0.85;

      &:hover {
        background: #171e49;
        color: #ebeefd;
        opacity: 1;
      }
    }
  }

  .fc-daygrid-more-link {
    background: rgba(57, 178, 75, 0.22) !important;
    color: #86efac !important;
  }

  .fc-daygrid-event {
    color: #f1f5f9 !important;
  }

  .fc-daygrid-event b,
  .fc-daygrid-event i,
  .fc-daygrid-event span {
    color: #f1f5f9 !important;
  }

  .fc-event-count {
    background: rgba(255, 255, 255, 0.25) !important;
    color: #ffffff !important;
  }

  .custom-tooltip {
    background: #1e1e2d;
    color: #cbd5e1;
    border-color: #334155;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6);
  }

  .tooltip-header {
    color: #f1f5f9;
  }

  .tooltip-date-sub {
    color: #94a3b8;
    border-bottom-color: #334155;
  }

  .tooltip-item-title,
  .tooltip-task-text {
    color: #e2e8f0;
  }

  .tooltip-task-item:hover {
    background: #2a2a3e;
  }

  .tooltip-list::-webkit-scrollbar-track {
    background: #1e1e2d;
  }

  .tooltip-list::-webkit-scrollbar-thumb {
    background: #475569;
  }

  .tooltip-more-note {
    color: #60a5fa;
    border-top-color: #334155;
  }

  .tooltip-more-note:hover {
    color: #93c5fd;
  }

  .fc-popover {
    background: #1e1e2d !important;
    border-color: #334155 !important;
  }

  .fc-popover-header {
    background: #28283c !important;
    color: #f1f5f9 !important;
    border-bottom-color: #334155 !important;
  }

  .fc-popover-body {
    background: #1e1e2d !important;
  }

  .grouped-task-card {
    background: #28283c;
    border-color: #383852;
  }

  .grouped-task-card:hover {
    background: #32324d;
  }

  .grouped-task-card .text-dark {
    color: #f1f5f9 !important;
  }

  .preset-trigger-btn {
    background-color: #262c49;
    border-color: #414561;
    color: #c2c6dc;

    &:hover {
      background-color: #171e49;
      border-color: #39b24b;
    }
  }

  .schedule-preset-dropdown-menu {
    background-color: #262c49 !important;
    border-color: #414561 !important;

    .preset-section-title {
      color: #7e8299;
    }

    .preset-custom-section {
      border-top-color: #383e60;
    }

    .preset-item {
      color: #c2c6dc;

      &:hover {
        background-color: #171e49;
        color: #ebeefd;
      }

      &.is-active-preset {
        background-color: rgba(57, 178, 75, 0.2);
        color: #86efac;

        .preset-item-icon {
          color: #86efac !important;
        }
      }
    }

    .preset-default-badge {
      background-color: rgba(57, 178, 75, 0.25);
      color: #86efac;
    }

    .preset-dropdown-footer {
      background-color: #1c2242;
      border-top-color: #383e60;
    }

    .save-preset-btn {
      background-color: #262c49;
      border-color: #39b24b;
      color: #86efac;

      &:hover {
        background-color: #39b24b;
        color: #ffffff;
      }
    }
  }

  .current-filters-preview {
    background: #262c49;
    border-color: #414561;

    .text-dark {
      color: #f1f5f9 !important;
    }
  }
}
</style>